"use strict";
/*
 * Copyright Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildNodeInstances = exports.calculateNodeRadiusForZoom = void 0;
const maplibre_gl_1 = require("maplibre-gl");
const turf_1 = require("@turf/turf");
const geojson_validation_1 = require("geojson-validation");
const ColorUtils_1 = require("chaire-lib-common/lib/utils/ColorUtils");
/**
 * Calculate radius for selected nodes based on zoom level (exponential interpolation between the
 * stops 0 px at zoom 0, 3 px at 10, 10 px at 15 and 20 px at 20, then linear)
 * @param zoom - Current map zoom
 * @returns Radius in pixels
 */
const calculateNodeRadiusForZoom = (zoom) => {
    if (zoom <= 10) {
        return 0 + (3 - 0) * (Math.pow(2, (zoom - 0) / (10 - 0)) - 1);
    }
    else if (zoom <= 15) {
        return 3 + (10 - 3) * (Math.pow(2, (zoom - 10) / (15 - 10)) - 1);
    }
    else if (zoom <= 20) {
        return 10 + (20 - 10) * (Math.pow(2, (zoom - 15) / (20 - 15)) - 1);
    }
    else {
        return 20 + (zoom - 20) * 3;
    }
};
exports.calculateNodeRadiusForZoom = calculateNodeRadiusForZoom;
/** Fallback color for nodes without a valid `color` property */
const DEFAULT_COLOR_HEX = '#808080';
/** Whether a feature is a valid GeoJSON Point feature */
const isPointFeature = (feature) => (0, geojson_validation_1.isFeature)(feature) && (0, geojson_validation_1.isPoint)(feature.geometry);
/**
 * Build the instance buffers of the node spinner layer. Features that are not points are skipped.
 *
 * @param features GeoJSON features, Points with an optional `color` property (hex or rgb(a) string)
 * @returns The instance data, with the origin at the center of the nodes' bounding box
 */
const buildNodeInstances = (features) => {
    const mercatorPoints = features.filter(isPointFeature).map((feature) => {
        var _a;
        return ({
            mercator: maplibre_gl_1.MercatorCoordinate.fromLngLat([feature.geometry.coordinates[0], feature.geometry.coordinates[1]]),
            color: (0, ColorUtils_1.hexToRgbArray)((_a = feature.properties) === null || _a === void 0 ? void 0 : _a.color, DEFAULT_COLOR_HEX)
        });
    });
    const count = mercatorPoints.length;
    const positions = new Float32Array(count * 2);
    const colors = new Uint8Array(count * 4);
    if (count === 0) {
        return { origin: [0, 0], positions, colors, count };
    }
    const [minX, minY, maxX, maxY] = (0, turf_1.bbox)((0, turf_1.multiPoint)(mercatorPoints.map((point) => [point.mercator.x, point.mercator.y])));
    const origin = [(minX + maxX) / 2, (minY + maxY) / 2];
    mercatorPoints.forEach((point, i) => {
        positions[i * 2] = point.mercator.x - origin[0];
        positions[i * 2 + 1] = point.mercator.y - origin[1];
        colors.set(point.color, i * 4);
    });
    return { origin, positions, colors, count };
};
exports.buildNodeInstances = buildNodeInstances;
//# sourceMappingURL=nodeGeometry.js.map